export interface CageData {
    count: number;
    depthAvg: number;
    depthMax: number;
    depthMin: number;
    femaleAvg?: number;
    caligusElongatusAvg?: number;
    movingStageAvg?: number;
    livingWeight: number;
    /** Null when the report has no total for the pen. */
    femaleTotal?: number | null;
    caligusElongatusTotal?: number | null;
    movingStageTotal?: number | null;
    isIncluded: boolean;
}

export interface CalculateAverageInput {
    cages: CageData[];
}

export interface AverageResult {
    count: number;
    depthAvg: number;
    depthMax: number;
    depthMin: number;
    femaleAvg: number;
    caligusElongatusAvg?: number;
    movingStageAvg: number;
    livingWeight: number;
    femaleTotal: number;
    caligusElongatusTotal?: number;
    movingStageTotal: number;
    name?: string;
}

interface AverageCalculationOptions {
    includeCaligusElongatus: boolean;
}

/** A cage's share of an average; a cage without a finite value adds nothing. */
function finiteShare(value: number | null | undefined, divider: number): number {
    return typeof value === 'number' && Number.isFinite(value) ? value / divider : 0;
}

const createInitialResult = (options: AverageCalculationOptions): Omit<AverageResult, 'name'> => ({
    count: 0,
    depthAvg: 0,
    depthMax: 0,
    depthMin: 0,
    femaleAvg: 0,
    caligusElongatusAvg: options.includeCaligusElongatus ? 0 : undefined,
    movingStageAvg: 0,
    livingWeight: 0,
    femaleTotal: 0,
    caligusElongatusTotal: options.includeCaligusElongatus ? 0 : undefined,
    movingStageTotal: 0,
});

const calculateAverages = (
    input: CalculateAverageInput,
    options: AverageCalculationOptions
): AverageResult => {
    const includedCages = input.cages.filter((c) => c.isIncluded);
    const depthAvgDivider = includedCages.filter((c) => c.depthAvg).length;
    const cageCount = includedCages.length;
    const finiteAvgCount = (
        value: keyof Pick<CageData, 'femaleAvg' | 'movingStageAvg' | 'caligusElongatusAvg'>
    ) => includedCages.filter((c) => Number.isFinite(c[value])).length || 1;
    const femaleAvgDivider = finiteAvgCount('femaleAvg');
    const movingStageAvgDivider = finiteAvgCount('movingStageAvg');
    const caligusAvgDivider = finiteAvgCount('caligusElongatusAvg');

    return includedCages.reduce((res, curr) => {
        if (!curr.isIncluded) return res;

        const result = {
            ...res,
            count: res.count + curr.count,
            depthAvg: res.depthAvg + curr.depthAvg / depthAvgDivider,
            depthMax: res.depthMax + curr.depthMax / depthAvgDivider,
            depthMin: res.depthMin + curr.depthMin / depthAvgDivider,
            femaleAvg: res.femaleAvg + finiteShare(curr.femaleAvg, femaleAvgDivider),
            movingStageAvg:
                res.movingStageAvg + finiteShare(curr.movingStageAvg, movingStageAvgDivider),
            livingWeight: res.livingWeight + curr.livingWeight / cageCount,
            femaleTotal: res.femaleTotal + (curr.femaleTotal || 0),
            movingStageTotal: res.movingStageTotal + (curr.movingStageTotal || 0),
        };

        if (options.includeCaligusElongatus) {
            result.caligusElongatusAvg =
                (result.caligusElongatusAvg || 0) +
                finiteShare(curr.caligusElongatusAvg, caligusAvgDivider);
            result.caligusElongatusTotal =
                (result.caligusElongatusTotal || 0) + (curr.caligusElongatusTotal || 0);
        }

        return result;
    }, createInitialResult(options));
};

const calculateAverage = (input: CalculateAverageInput): AverageResult =>
    calculateAverages(input, { includeCaligusElongatus: true });

const calculateAverageChileanLice = (input: CalculateAverageInput): AverageResult =>
    calculateAverages(input, { includeCaligusElongatus: false });

export default calculateAverage;
export { calculateAverage, calculateAverageChileanLice };
